import React, { useEffect, useState } from "react";
import classnames from "classnames";
import { observer } from "mobx-react";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { apiUrls } from "modules/common/services/communication/urls";
import { Banner } from "modules/root/components/banner/Banner";
import { useMst } from "modules/root/models/hooks";
import { can } from "modules/session/auth/access";
import func from "../../functionalities";
import { plural } from "modules/spending/departments/org-tree/components/context";
import { NewsDialog } from "./NewsDialog";
import styles from "./NewsBanners.module.scss";

/** Вид новости (NewsKinds на сервере) */
export const NEWS_VERSION = "version";

/** Текст длиннее — на плашке обрезается, целиком открывается по «Подробнее» */
const SHORT_TEXT = 300;

/**
 * Плашки новостей в общей стопке на любой странице — только сотрудникам. Какие новости
 * показывать, решает сервер (`news/banners`): непрочитанные объявления с действующим сроком
 * и последняя новость о версии. Крестик и закрытие окна «Подробнее» отмечают новость прочитанной
 */
export const NewsBanners = observer(() => {
    const store = useMst();
    const internal = can(func.INTERNAL_USER_ACCESS, store.session.access);
    const [items, setItems] = useState<BannerNews[]>([]);
    // прочитанные остаются в списке закрытыми: так плашка успевает уехать анимацией
    const [closed, setClosed] = useState<string[]>([]);
    const [details, setDetails] = useState<BannerNews | null>(null);

    useEffect(() => {
        if (!internal) {
            return;
        }

        let alive = true;

        store.transport
            .get<BannerNews[]>(apiUrls.news.banners())
            .then((data) => alive && setItems(data || []))
            .catch(() => {
                // не загрузились — без плашек, новости остаются в виджете на Главной
            });

        return () => {
            alive = false;
        };
    }, [store, internal]);

    const read = (news: BannerNews) => {
        setClosed((ids) => [...ids, news.id]);

        // если новость уже загружена в виджет на Главной — отмечаем через неё, чтобы
        // сразу уменьшился счётчик непрочитанных
        const loaded = store.newsWidget.asMap[news.id];
        const request = loaded ? loaded.open() : store.transport.post(apiUrls.news.open(news.id));

        void Promise.resolve(request).catch(() => {
            // отметка не сохранилась — плашка покажется при следующем открытии
        });
    };

    return (
        <>
            {items.map((news) => {
                const version = news.kind === NEWS_VERSION;
                const long = version || plain(news.text).length > SHORT_TEXT;

                return (
                    <Banner
                        key={news.id}
                        open={!closed.includes(news.id)}
                        label={news.name}
                        title={news.name}
                        onClose={() => read(news)}
                        order={version ? 5 : 0}
                    >
                        {news.image && (
                            <div className={styles.image}>
                                <img src={news.image} alt="" draggable={false} />
                            </div>
                        )}
                        {version ? (
                            <div>{versionSummary(news.text)}</div>
                        ) : (
                            <div
                                className={classnames(styles.text, { [styles.clamped]: long })}
                                dangerouslySetInnerHTML={{ __html: news.text }}
                            />
                        )}
                        {long && (
                            <div className={styles.actions}>
                                <PlanrButton type="primary" size="small" onClick={() => setDetails(news)}>
                                    Подробнее
                                </PlanrButton>
                            </div>
                        )}
                    </Banner>
                );
            })}
            {details && (
                <NewsDialog
                    news={details}
                    onClose={() => {
                        read(details);
                        setDetails(null);
                    }}
                />
            )}
        </>
    );
});

/**
 * Строка плашки о версии с числом изменений — пунктов верхнего уровня в списке изменений
 * (текст новости о версии — запись CHANGELOG)
 */
export const versionSummary = (html: string) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const count = doc.body.querySelectorAll(":scope > ol > li, :scope > ul > li").length;

    return count > 0
        ? `Вышло обновление РостПро: ${count} ${plural(count, "изменение", "изменения", "изменений")}`
        : "Вышло обновление РостПро";
};

/** Текст новости без разметки — чтобы понять, помещается ли он на плашку */
const plain = (html: string) => html.replace(/<[^>]*>/g, "").trim();

interface BannerNews {
    id: string;
    name: string;
    text: string;
    image: string | null;
    kind: string | null;
}
